import { Component, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EmailTemplateDesignerComponent } from '@features/settings/template-builder/email-template-designer/email-template-designer.component';
import {
    BaseTemplateModel,
    EmailTemplateModel,
    OfferingNotificationType
} from '@features/settings/template-builder/model/offering-template.model';
import {
    AtokuEmailTemplateDocument,
    AtokuEmailTemplateUpdateRequest
} from '../../../services/atoku.service';

export interface EmailTemplateDialogData {
    document: AtokuEmailTemplateDocument;
    title: string;
}

@Component({
    selector: 'app-atoku-email-template-dialog',
    templateUrl: './email-template-dialog.component.html',
    styleUrls: ['./email-template-dialog.component.scss']
})
export class EmailTemplateDialogComponent {
    @ViewChild('emailDesigner') emailDesigner?: EmailTemplateDesignerComponent;

    readonly form: FormGroup;
    readonly template: BaseTemplateModel;
    saving = false;

    constructor(
        private readonly fb: FormBuilder,
        private readonly dialogRef: MatDialogRef<EmailTemplateDialogComponent, AtokuEmailTemplateUpdateRequest>,
        @Inject(MAT_DIALOG_DATA) public readonly data: EmailTemplateDialogData
    ) {
        this.form = this.fb.group({
            name: [data.document.name || data.title],
            subject: [data.document.subject || data.title],
            previewText: [data.document.previewText || '']
        });

        this.template = {
            uuid: data.document.documentId ?? data.document.documentKey,
            templateName: data.document.name || data.title,
            templateCode: data.document.documentKey,
            active: true,
            recipientList: [],
            design: this.parseDesign(data.document.designJson),
            html: data.document.html || '',
            mergeTags: [],
            type: OfferingNotificationType.SYSTEM
        };
    }

    get documentStatus(): string {
        if (!this.data.document.exists) {
            return 'New template';
        }

        return `Version ${this.data.document.version || 1}`;
    }

    cancel(): void {
        if (!this.saving) {
            this.dialogRef.close();
        }
    }

    save(): void {
        if (!this.emailDesigner || this.saving) {
            return;
        }

        this.saving = true;
        this.template.templateName = this.getString('name') || this.data.title;
        this.emailDesigner.saveAndExportHtml();
    }

    handleSelectedTemplateChange(template: EmailTemplateModel | null): void {
        if (!template) {
            return;
        }

        this.template.design = template.design;
        this.template.html = template.html;
    }

    handleSaveTemplateResult(success: boolean): void {
        if (!success) {
            this.saving = false;
            return;
        }

        this.dialogRef.close({
            name: this.getString('name') || this.data.title,
            subject: this.getString('subject') || this.data.title,
            previewText: this.getString('previewText'),
            html: this.template.html || '',
            designJson: JSON.stringify(this.template.design ?? {})
        });
    }

    private getString(controlName: string): string {
        return String(this.form.get(controlName)?.value ?? '').trim();
    }

    private parseDesign(rawDesign: string | null | undefined): unknown {
        if (!rawDesign) {
            return this.blankDesign();
        }

        try {
            const parsed = JSON.parse(rawDesign);
            return parsed && typeof parsed === 'object' && 'body' in parsed ? parsed : this.blankDesign();
        } catch {
            return this.blankDesign();
        }
    }

    private blankDesign(): unknown {
        return {
            counters: {
                u_row: 1,
                u_column: 1
            },
            body: {
                rows: [],
                values: {
                    backgroundColor: '#ffffff',
                    contentWidth: '600px',
                    fontFamily: {
                        label: 'Arial',
                        value: 'arial,helvetica,sans-serif'
                    }
                }
            }
        };
    }
}
